import { ChangeEvent, useCallback, useRef, useState } from 'react';

import { Button, Typography, makeStyles } from '@material-ui/core';

import { RiUploadLine } from '@remixicon/react';
import type { Replayer, eventWithTime } from 'rrweb';

import { PageLayout } from '@spotify/confidence-core-react';
import { SessionReplayPlayer } from '@spotify/confidence-session-replay-react';
import { withPageImpression } from '@spotify/plugin-confidence-tracking-react';

import { EventLog } from '../EventLog';
import { SessionSidebar } from '../SessionSidebar';

const useStyles = makeStyles({
  upload: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--bui-space-3)',
    padding: 'var(--bui-space-12)',
    border: '2px dashed var(--bui-border-disabled)',
    borderRadius: 'var(--bui-radius-2)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background 0.2s',
    '&:hover': {
      borderColor: 'var(--bui-fg-link)',
    },
  },
  uploadIcon: {
    color: 'var(--bui-fg-disabled)',
    marginBottom: 'var(--bui-space-2)',
  },
});

const SessionReplayPageContent = () => {
  const classes = useStyles();
  const [events, setEvents] = useState<eventWithTime[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replayerRef = useRef<Replayer | null>(null);

  const handleReplayerReady = useCallback((replayer: Replayer) => {
    replayerRef.current = replayer;
  }, []);

  const handleEventClick = useCallback((timeOffset: number) => {
    if (replayerRef.current) {
      replayerRef.current.pause(timeOffset);
    }
  }, []);

  const handleReset = () => {
    setEvents(null);
    setError(null);
    replayerRef.current = null;
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        const eventArray = Array.isArray(parsed) ? parsed : parsed.events;
        if (!Array.isArray(eventArray)) {
          setError('File must contain a JSON array of rrweb events.');
          return;
        }
        setEvents(eventArray);
      } catch {
        setError('Failed to parse JSON file.');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file.');
    };
    reader.readAsText(file);
  };

  if (!events) {
    return (
      <PageLayout title="Session replay">
        <div
          className={classes.upload}
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              fileInputRef.current?.click();
            }
          }}
        >
          <RiUploadLine size={32} className={classes.uploadIcon} />
          <Typography variant="h6">Upload a session recording</Typography>
          <Typography variant="body2" color="textSecondary">
            Drop a JSON file or click to browse
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Session replay"
      headerContent={
        <Button
          variant="text"
          color="default"
          size="small"
          startIcon={<RiUploadLine size={16} />}
          onClick={handleReset}
        >
          Upload a different recording
        </Button>
      }
      sideBarRight={<SessionSidebar events={events} />}
    >
      <SessionReplayPlayer
        events={events}
        onReplayerReady={handleReplayerReady}
      />
      <EventLog events={events} onEventClick={handleEventClick} />
    </PageLayout>
  );
};

export const SessionReplayPage = withPageImpression(SessionReplayPageContent, {
  pageId: 'session-replay',
});
